/* =============================================================================
 *  AndreiChatContext
 *
 *  Global open/close state + optional "context" handle for the Andrei chat
 *  panel. Lives at App level so any page can call `openAndreiChat({ context })`
 *  to launch the chat preloaded with what the user is looking at — e.g. from
 *  a job workspace modal:
 *
 *    const { openAndreiChat } = useAndreiChat();
 *    openAndreiChat({
 *      context: { type: 'job', id: jobId, label: `Job ${jobNumber}` },
 *      prefill: 'about this job: ',
 *    });
 *
 *  The chat panel renders a removable chip for the context and includes it
 *  as a `context` field on each outgoing andrei_messages doc, so Andrei's
 *  cloud function can scope its answer to the right entity.
 *
 *  Not every handle is a document. The calendar passes a DATE handle:
 *
 *    openAndreiChat({
 *      context: { type: 'calendar', id: '2026-08-03', date: '2026-08-03',
 *                 label: 'Mon 3 Aug 2026' },
 *      prefill: "What's on for Monday 3 August 2026 (2026-08-03)? ",
 *    });
 *
 *  Callers of the calendar handle deliberately restate the date in `prefill`
 *  too: until the backend maps `type: 'calendar'` into the system prompt (see
 *  the note on `date` below), the message text is the only thing that actually
 *  reaches the model, and the button has to stay useful either way.
 *
 *  History: this context originally backed the OpenClaw "Steve" bridge. We
 *  pivoted to using the existing internal LLM (the default Virtual Employee
 *  named "Andrei") and rebranded the surface. The wire format is identical,
 *  only the message bus collection name changed (steve_messages → andrei_messages).
 * ============================================================================= */

import React, { createContext, useCallback, useContext, useState } from 'react';

/**
 * What kind of thing the chat is currently scoped to.
 *
 * `'calendar'` (added for the calendar's Andrei door) is a DATE-shaped handle
 * rather than a document-shaped one: a day, or an inclusive span of days, that
 * the user is looking at on the calendar — optionally narrowed to one event.
 */
export type AndreiChatContextKind =
  | 'job' | 'customer' | 'invoice' | 'bill' | 'email' | 'calendar' | 'other';

export interface AndreiChatContextHandle {
  type:  AndreiChatContextKind;
  id:    string;
  label?: string;

  // ── Calendar handles only ────────────────────────────────────────────────
  // The union used to be closed over document types, so a surface that wanted
  // to say "the user is asking about Thursday" had no slot for a date and had
  // to fall back to `other` — which the backend cannot scope on at all.
  //
  // `id` stays required and stays a plain string so the wire format, the chip
  // in the panel and the `context` field written onto each andrei_messages doc
  // are all unchanged: for a single day it is the 'YYYY-MM-DD', for a span it
  // is 'YYYY-MM-DD..YYYY-MM-DD'. `date`/`endDate` carry the same information in
  // a form the backend does not have to re-parse.
  /** Local 'YYYY-MM-DD' (Adelaide) the handle starts at. */
  date?: string;
  /** Inclusive local 'YYYY-MM-DD' end of a multi-day span. Absent = single day. */
  endDate?: string;
  /** The `events/{id}` document, when exactly one event is in focus. */
  eventId?: string;
}

interface OpenOptions {
  context?: AndreiChatContextHandle | null;
  prefill?: string;
  /** Optional session id. When set, the panel reads/writes only
   *  messages belonging to this session and shows the session title
   *  in the header. When null/undefined, the panel falls back to the
   *  global rolling feed (legacy behaviour). */
  sessionId?: string | null;
}

interface AndreiChatContextValue {
  isOpen: boolean;
  context: AndreiChatContextHandle | null;
  /** Currently-active conversation session, or null for the global feed. */
  sessionId: string | null;
  // The chat panel reads `pendingPrefill` once on open and clears it via
  // `consumePrefill()` so it doesn't overwrite the user's typing on
  // subsequent re-renders.
  pendingPrefill: string | null;
  consumePrefill: () => string | null;
  setContext: (ctx: AndreiChatContextHandle | null) => void;
  setSessionId: (id: string | null) => void;
  openAndreiChat: (opts?: OpenOptions) => void;
  closeAndreiChat: () => void;
}

const noop = () => {};
const AndreiChatContext = createContext<AndreiChatContextValue>({
  isOpen: false,
  context: null,
  sessionId: null,
  pendingPrefill: null,
  consumePrefill: () => null,
  setContext: noop,
  setSessionId: noop,
  openAndreiChat: noop,
  closeAndreiChat: noop,
});

export const AndreiChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen]                 = useState(false);
  const [context, setContext]               = useState<AndreiChatContextHandle | null>(null);
  const [sessionId, setSessionId]           = useState<string | null>(null);
  const [pendingPrefill, setPendingPrefill] = useState<string | null>(null);

  const openAndreiChat = useCallback((opts?: OpenOptions) => {
    if (opts && Object.prototype.hasOwnProperty.call(opts, 'context')) {
      setContext(opts.context ?? null);
    }
    // sessionId is explicit when opening — passing undefined leaves the
    // current session alone (e.g. someone clicks [andrei] from the
    // header with no opts), but passing null actively switches back to
    // the global feed.
    if (opts && Object.prototype.hasOwnProperty.call(opts, 'sessionId')) {
      setSessionId(opts.sessionId ?? null);
    }
    if (opts?.prefill !== undefined) {
      setPendingPrefill(opts.prefill || null);
    }
    setIsOpen(true);
  }, []);

  const closeAndreiChat = useCallback(() => {
    setIsOpen(false);
    // Context AND sessionId are sticky across close/reopen unless
    // explicitly cleared. Typical workflow: "open chat about job 1234,
    // ask one question, close, reopen later for the same job".
  }, []);

  const consumePrefill = useCallback(() => {
    const v = pendingPrefill;
    if (v != null) setPendingPrefill(null);
    return v;
  }, [pendingPrefill]);

  return (
    <AndreiChatContext.Provider
      value={{
        isOpen, context, sessionId, pendingPrefill,
        consumePrefill, setContext, setSessionId,
        openAndreiChat, closeAndreiChat,
      }}
    >
      {children}
    </AndreiChatContext.Provider>
  );
};

export const useAndreiChat = (): AndreiChatContextValue => useContext(AndreiChatContext);
